// Persists ONLY the master-key envelopes (user_key_envelopes). Attribute ciphertext
// lives in user_attributes (see attributesService). user_id is taken from the
// session, never client args; RLS double-enforces owner-only access.

import { supabase } from '../supabaseClient';
import { KeyEnvelopeRecord, WrappedKey } from './types';

const TABLE = 'user_key_envelopes';

export class VaultTableMissingError extends Error {
  constructor() {
    super('The vault key store is not set up yet. Run supabase/datafi.sql.');
    this.name = 'VaultTableMissingError';
  }
}

function isMissingTable(error: { code?: string; message?: string } | null): boolean {
  if (!error) return false;
  return error.code === '42P01' || error.code === 'PGRST205' ||
    /relation .*user_key_envelopes.* does not exist/i.test(error.message || '');
}

async function requireUserId(): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  return user.id;
}

function rowToRecord(row: any): KeyEnvelopeRecord {
  return {
    userId: row.user_id,
    envelopePin: (row.envelope_pin as WrappedKey) ?? null,
    pinSalt: row.pin_salt ?? null,
    envelopePasskey: (row.envelope_passkey as WrappedKey) ?? null,
    passkeyId: row.passkey_id ?? null,
    updatedAt: row.updated_at ?? null,
  };
}

export async function fetchKeyEnvelopes(): Promise<KeyEnvelopeRecord | null> {
  const userId = await requireUserId();
  const { data, error } = await supabase.from(TABLE).select('*').eq('user_id', userId).maybeSingle();
  if (error) {
    if (isMissingTable(error)) throw new VaultTableMissingError();
    throw error;
  }
  return data ? rowToRecord(data) : null;
}

interface PersistInput {
  envelopePin: WrappedKey | null;
  pinSalt: string | null;
  envelopePasskey: WrappedKey | null;
  passkeyId: string | null;
}

// Writes the complete envelope set in one upsert (atomic).
export async function persistKeyEnvelopes(input: PersistInput): Promise<KeyEnvelopeRecord> {
  const userId = await requireUserId();
  const { data, error } = await supabase
    .from(TABLE)
    .upsert({
      user_id: userId,
      envelope_pin: input.envelopePin,
      pin_salt: input.pinSalt,
      envelope_passkey: input.envelopePasskey,
      passkey_id: input.passkeyId,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id' })
    .select()
    .single();
  if (error) {
    if (isMissingTable(error)) throw new VaultTableMissingError();
    throw error;
  }
  return rowToRecord(data);
}

export async function persistPinEnvelope(env: WrappedKey, salt: string): Promise<KeyEnvelopeRecord> {
  const current = await fetchKeyEnvelopes();
  return persistKeyEnvelopes({
    envelopePin: env,
    pinSalt: salt,
    envelopePasskey: current?.envelopePasskey ?? null,
    passkeyId: current?.passkeyId ?? null,
  });
}

export async function persistPasskeyEnvelope(env: WrappedKey, passkeyId: string): Promise<KeyEnvelopeRecord> {
  const current = await fetchKeyEnvelopes();
  return persistKeyEnvelopes({
    envelopePin: current?.envelopePin ?? null,
    pinSalt: current?.pinSalt ?? null,
    envelopePasskey: env,
    passkeyId,
  });
}

export async function removePasskeyEnvelope(): Promise<KeyEnvelopeRecord> {
  const current = await fetchKeyEnvelopes();
  return persistKeyEnvelopes({
    envelopePin: current?.envelopePin ?? null,
    pinSalt: current?.pinSalt ?? null,
    envelopePasskey: null,
    passkeyId: null,
  });
}
